"use client"

import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
} from "@ionic/react"
import { add } from "ionicons/icons"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Button,
  Chip,
  Box,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Avatar,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Slide,
} from "@mui/material"
import {
  Delete,
  Block,
  CheckCircle,
  LockReset,
  Visibility,
  VisibilityOff,
  PersonAdd,
  Group,
  Edit,
  Refresh,
  Warning,
  Info,
  Error as ErrorIcon,
  PersonRemove,
  PlayArrow,
  Pause,
  Lock,
  LockOpen,
} from "@mui/icons-material"
import { createStaff_crm, getStaffList_crm } from "../../../api/users/user"
import axios from "axios"
import { requestID, baseUrl, config } from "../../../common/utils"
import { TransitionProps } from "@mui/material/transitions"
import React from "react"
import { MenuButton } from "../../../components/Menu"

interface Staff {
  id: number
  user_id: string
  firstName: string
  lastName: string
  phone: string
  email: string
  status: string | "active" | "suspended"
  createdAt: string
  username?: string
  device_check_enabled?: boolean
}

interface NewStaff {
  firstName: string
  lastName: string
  phone: string
  email: string
  password: string
  role_id?: string
  username?: string
}

interface Role {
  id: string
  name: string
  description?: string
}

// Custom transition for dialogs
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />
})

const StaffManagementCRM: React.FC = () => {
  const [staffList, setStaffList] = useState<Staff[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditPasswordModalOpen, setIsEditPasswordModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(true)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null)
  
  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState({ open: false, staff: null as Staff | null })
  const [statusDialog, setStatusDialog] = useState({ open: false, staff: null as Staff | null })
  const [deviceLockDialog, setDeviceLockDialog] = useState({ open: false, staff: null as Staff | null, action: "enable" as "enable" | "disable" })
  const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", type: "info" as "info" | "warning" | "error" | "success" })
  
  // Loading states for dialog actions
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(false)
  const [deviceLockLoading, setDeviceLockLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  
  const [newStaff, setNewStaff] = useState<NewStaff>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    role_id: "",
    username: ""
  })

  const [editPasswordData, setEditPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  })

  // Color palette
  const colors = {
    primary: "#2196f3",
    secondary: "#ff4081",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#00bcd4",
    background: "#fafafa",
    card: "#ffffff",
    textPrimary: "#212121",
    textSecondary: "#757575",
  }

  useEffect(() => {
    getRoles()
    fetchStaff()
  }, [])

  const fetchStaff = async () => {
    try {
      setFetchLoading(true)
      setError(null)
      const response = await getStaffList_crm()
      const data = await response.data
      setStaffList(data)
      console.log("Fetched staff:", data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch staff")
      console.error("Error fetching staff:", err)
    } finally {
      setFetchLoading(false)
    }
  }

  const getRoles = async () => {
    try {
      setRolesLoading(true)
      const formData = new FormData()
      formData.append("requestID", requestID())
      
      const response = await axios.post(
        baseUrl() + "businesss/getRoles4",
        formData,
        config
      )
      setRoles(response.data.data)
      console.log("Fetched roles:", response)
    } catch (err) {
      console.error("Error fetching roles:", err)
      setError("Failed to load roles")
    } finally {
      setRolesLoading(false)
    }
  }

  const handleAddStaff = async () => {
    if (
      !newStaff.firstName ||
      !newStaff.phone ||
      !newStaff.email ||
      !newStaff.password ||
      !newStaff.role_id ||
      !newStaff.username
    ) {
      showAlertDialog("Missing Information", "Please fill in all required fields", "warning");
      return;
    }

    setLoading(true);
    try {
      const res = await createStaff_crm(newStaff);

      if (res.status !== "true") {
        showAlertDialog("Error", res.message || "Username or email already exists", "error");
        setLoading(false);
        return; // ❗ Stop here
      }

      // ✅ If status is true → proceed
      fetchStaff();
      setNewStaff({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        password: "",
        role_id: "",
        username: ""
      });
      setIsModalOpen(false);
      showAlertDialog("Success", "Staff member added successfully", "success");

    } catch (err) {
      showAlertDialog(
        "Error",
        err instanceof Error ? err.message : "Failed to add staff",
        "error"
      );
      console.error("Error adding staff:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceLockToggle = (staff: Staff) => {
    const action = staff.device_check_enabled ? "disable" : "enable"
    setDeviceLockDialog({ open: true, staff, action })
  }

  const confirmDeviceLockToggle = async () => {
    if (!deviceLockDialog.staff) return

    const staff = deviceLockDialog.staff
    const action = deviceLockDialog.action // "enable" or "disable"
    
    setDeviceLockLoading(true)
    try {
      const formData = new FormData()
      formData.append("requestID", requestID())
      formData.append("user_id", staff.user_id)
      formData.append("device_check", action === "enable" ? "1" : "0") // 1 for enable, 0 for disable
      
      // Replace with your actual endpoint
      const response = await axios.post(
        baseUrl() + "users/updateDeviceCheck", // Update this endpoint
        formData,
        config
      )

      if (response.data.status) {
        // Update local state immediately
        setStaffList(staffList.map(s => 
          s.user_id === staff.user_id 
            ? { ...s, device_check_enabled: action === "enable" } 
            : s
        ))
        
        showAlertDialog(
          "Device Locking Updated", 
          `Device locking has been ${action === "enable" ? "enabled" : "disabled"} for ${staff.firstName} ${staff.lastName}`,
          "success"
        )
      } else {
        throw new Error(response.data.message || "Failed to update device locking")
      }
    } catch (err) {
      showAlertDialog("Error", err instanceof Error ? err.message : "Failed to update device locking", "error")
      console.error("Error updating device locking:", err)
    } finally {
      setDeviceLockLoading(false)
      setDeviceLockDialog({ open: false, staff: null, action: "enable" })
    }
  }

  const handleStatusToggle = (staff: Staff) => {
    setStatusDialog({ open: true, staff })
  }

  const confirmStatusToggle = async () => {
    if (!statusDialog.staff) return

    const staff = statusDialog.staff
    const currentStatus = getStatusValue(staff.status)
    const newStatus = currentStatus === "active" ? "suspended" : "activate"
    
    setStatusLoading(true)
    try {
      const formData = new FormData()
      formData.append("requestID", requestID())
      formData.append("staff_id", staff.user_id)
      formData.append("action", newStatus)
      
      const response = await axios.post(
        baseUrl() + "businesss/suspendStaff",
        formData,
        config
      )

      if (response.status) {
        fetchStaff()
        showAlertDialog(
          "Status Updated", 
          `${staff.firstName} ${staff.lastName} has been ${newStatus === "suspended" ? "suspended" : "activated"}`,
          "success"
        )
      } else {
        throw new Error("Failed to update status")
      }
    } catch (err) {
      showAlertDialog("Error", err instanceof Error ? err.message : "Failed to update status", "error")
      console.error("Error updating status:", err)
    } finally {
      setStatusLoading(false)
      setStatusDialog({ open: false, staff: null })
    }
  }

  const handleDeleteStaff = (staff: Staff) => {
    setDeleteDialog({ open: true, staff })
  }

  const confirmDeleteStaff = async () => {
    if (!deleteDialog.staff) return

    setDeleteLoading(true)
    try {
      const formData = new FormData()
      formData.append("requestID", requestID())
      formData.append("user_id", deleteDialog.staff.user_id)
      
      const response = await axios.post(
        baseUrl() + "staffs/deleteStaff",
        formData,
        config
      )

      if (response.status) {
        setStaffList(staffList.filter(s => s.user_id !== deleteDialog.staff!.user_id))
        showAlertDialog(
          "Staff Deleted", 
          `${deleteDialog.staff.firstName} ${deleteDialog.staff.lastName} has been permanently deleted`,
          "success"
        )
      } else {
        throw new Error("Failed to delete staff")
      }
    } catch (err) {
      showAlertDialog("Error", err instanceof Error ? err.message : "Failed to delete staff", "error")
      console.error("Error deleting staff:", err)
    } finally {
      setDeleteLoading(false)
      setDeleteDialog({ open: false, staff: null })
    }
  }

  const handleEditPassword = (staff: Staff) => {
    setSelectedStaff(staff)
    setEditPasswordData({ newPassword: "", confirmPassword: "" })
    setIsEditPasswordModalOpen(true)
  }

  const confirmEditPassword = async () => {
    if (!selectedStaff) return

    if (editPasswordData.newPassword !== editPasswordData.confirmPassword) {
      showAlertDialog("Password Mismatch", "New password and confirmation password do not match", "warning")
      return
    }

    if (editPasswordData.newPassword.length < 6) {
      showAlertDialog("Password Too Short", "Password must be at least 6 characters long", "warning")
      return
    }

    setPasswordLoading(true)
    try {
      const data = {
        requestID: requestID(),
        user_id: selectedStaff.user_id,
        new_password: editPasswordData.newPassword
      }

      const response = await axios.post(
        baseUrl() + "users/updateStaffPassword",
        data,
        config
      )

      if (response.status) {
        showAlertDialog("Success", "Password updated successfully", "success")
        setIsEditPasswordModalOpen(false)
        setSelectedStaff(null)
        setEditPasswordData({ newPassword: "", confirmPassword: "" })
      } else {
        throw new Error("Failed to update password")
      }
    } catch (err) {
      showAlertDialog("Error", err instanceof Error ? err.message : "Failed to update password", "error")
      console.error("Error updating password:", err)
    } finally {
      setPasswordLoading(false)
    }
  }

  // Helper function to show alert dialog
  const showAlertDialog = (title: string, message: string, type: "info" | "warning" | "error" | "success") => {
    setAlertDialog({ open: true, title, message, type })
  }

  // Helper function to safely get status value
  const getStatusValue = (status: any): string => {
    if (typeof status === 'string') return status
    if (typeof status === 'number') return status === 1 ? 'active' : 'suspended'
    if (status === true) return 'active'
    if (status === false) return 'suspended'
    return 'unknown'
  }

  // Helper function to safely format status display
  const formatStatusDisplay = (status: any): string => {
    const statusValue = getStatusValue(status)
    return statusValue.charAt(0).toUpperCase() + statusValue.slice(1)
  }

  // Helper function to check if staff is active
  const isStaffActive = (status: any): boolean => {
    const statusValue = getStatusValue(status)
    return statusValue === 'active'
  }

  const getStatusColor = (status: any) => {
    return isStaffActive(status) ? colors.success : colors.error
  }

  const getDeviceLockColor = (enabled: boolean | undefined) => {
    return enabled ? colors.success : colors.textSecondary
  }

  const getDeviceLockIcon = (enabled: boolean | undefined) => {
    return enabled ? <Lock /> : <LockOpen />
  }

  const getDeviceLockText = (enabled: boolean | undefined) => {
    return enabled ? "Device Lock Enabled" : "Device Lock Disabled"
  }

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase()
  }

  const getDialogIcon = (type: string) => {
    switch (type) {
      case "warning": return <Warning sx={{ fontSize: 40, color: colors.warning }} />
      case "error": return <ErrorIcon sx={{ fontSize: 40, color: colors.error }} />
      case "success": return <CheckCircle sx={{ fontSize: 40, color: colors.success }} />
      default: return <Info sx={{ fontSize: 40, color: colors.info }} />
    }
  }

  const getDialogColor = (type: string) => {
    switch (type) {
      case "warning": return colors.warning
      case "error": return colors.error
      case "success": return colors.success
      default: return colors.info
    }
  }

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar style={{ background: colors.primary }}>
          <MenuButton/>
          <IonTitle style={{ color: 'white', fontWeight: 'bold' }}>
            Staff Management
          </IonTitle>
          <IonButton 
            slot="end" 
            onClick={() => setIsModalOpen(true)}
            style={{ 
              '--background': 'white', 
              '--color': colors.primary,
              '--border-radius': '8px',
              marginRight: '16px'
            } as any}
          >
            <IonIcon icon={add} slot="start" />
            Add Staff
          </IonButton>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ background: colors.background, padding: '14px' , marginBottom: '16px'}}  >
        {/* Header Cards */}
        <Grid container spacing={2} sx={{ mb: 4, mt: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.info})`,
              color: 'white',
              borderRadius: 2,
              boxShadow: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {staffList?.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Staff
                    </Typography>
                  </Box>
                  <Group sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${colors.success}, #66bb6a)`,
              color: 'white',
              borderRadius: 2,
              boxShadow: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {staffList?.filter(s => isStaffActive(s.status)).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Active Staff
                    </Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${colors.warning}, #ffb74d)`,
              color: 'white',
              borderRadius: 2,
              boxShadow: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {staffList?.filter(s => s.device_check_enabled).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Device Lock Enabled
                    </Typography>
                  </Box>
                  <Lock sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${colors.secondary}, #f06292)`,
              color: 'white',
              borderRadius: 2,
              boxShadow: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Actions
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Manage Staff
                    </Typography>
                  </Box>
                  <Refresh 
                    sx={{ fontSize: 40, opacity: 0.8, cursor: 'pointer' }} 
                    onClick={fetchStaff}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Staff Table */}
        <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold" color={colors.textPrimary}>
                Staff Members
              </Typography>
            </Box>

            {fetchLoading ? (
              <Box textAlign="center" py={4}>
                <CircularProgress sx={{ color: colors.primary }} />
                <Typography variant="body1" sx={{ mt: 2, color: colors.textSecondary }}>
                  Loading staff members...
                </Typography>
              </Box>
            ) : error ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
                <Button 
                  onClick={fetchStaff} 
                  variant="contained" 
                  startIcon={<Refresh />}
                  sx={{ background: colors.primary }}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Staff Member
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Contact Info
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Device Locking
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Created Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staffList?.map((staff) => (
                      <TableRow 
                        key={staff.user_id}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ 
                              bgcolor: colors.primary,
                              width: 40,
                              height: 40
                            }}>
                              {getInitials(staff.firstName, staff.lastName)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {staff.firstName} {staff.lastName}
                              </Typography>
                              <Typography variant="caption" color={colors.textSecondary}>
                                {staff.username}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {staff.phone}
                          </Typography>
                          <Typography variant="caption" color={colors.textSecondary}>
                            {staff.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatStatusDisplay(staff.status)}
                            size="small"
                            sx={{
                              background: getStatusColor(staff.status),
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={staff.device_check_enabled || false}
                                  onChange={() => handleDeviceLockToggle(staff)}
                                  color="success"
                                  size="small"
                                />
                              }
                              label={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  {getDeviceLockIcon(staff.device_check_enabled)}
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    {getDeviceLockText(staff.device_check_enabled)}
                                  </Typography>
                                </Box>
                              }
                              sx={{ margin: 0 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(staff.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Reset Password">
                              <IconButton
                                size="small"
                                onClick={() => handleEditPassword(staff)}
                                sx={{ color: colors.info }}
                              >
                                <LockReset />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isStaffActive(staff.status) ? "Deactivate Staff" : "Activate Staff"}>
                              <IconButton
                                size="small"
                                onClick={() => handleStatusToggle(staff)}
                                sx={{ 
                                  color: isStaffActive(staff.status) ? colors.warning : colors.success 
                                }}
                              >
                                {isStaffActive(staff.status) ? <Pause /> : <PlayArrow />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Staff">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteStaff(staff)}
                                sx={{ color: colors.error }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!fetchLoading && !error && staffList?.length === 0 && (
              <Box textAlign="center" py={6}>
                <Group sx={{ fontSize: 64, color: colors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color={colors.textSecondary} gutterBottom>
                  No Staff Members
                </Typography>
                <Typography variant="body2" color={colors.textSecondary} sx={{ mb: 3 }}>
                  Get started by adding your first staff member
                </Typography>
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  variant="contained"
                  startIcon={<PersonAdd />}
                  sx={{ background: colors.primary }}
                >
                  Add Staff Member
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Add Staff Modal */}
        <Dialog 
          open={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.info})`,
            color: 'white',
            py: 3
          }} className="pushDown">
            <Box display="flex" alignItems="center" gap={2}>
              <PersonAdd sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Add New Staff Member
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}   className="pushDown">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  value={newStaff.firstName}
                  onChange={(e) => setNewStaff({ ...newStaff, firstName: e.target.value })}
                  required
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  value={newStaff.lastName}
                  onChange={(e) => setNewStaff({ ...newStaff, lastName: e.target.value })}
                  required
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone Number"
                  fullWidth
                  value={newStaff.phone}
                  onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                  required
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={newStaff.email}
                  onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                  required
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Username"
                  fullWidth
                  value={newStaff.username}
                  onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value })}
                  required
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={newStaff.password}
                  onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                  required
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required variant="outlined">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={newStaff.role_id}
                    onChange={(e) => setNewStaff({ ...newStaff, role_id: e.target.value })}
                    label="Role"
                  >
                    <MenuItem value="">Select a role</MenuItem>
                    {roles?.map((role) => (
                      <MenuItem key={role.id} value={role.roleID}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setIsModalOpen(false)}
              variant="outlined"
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: colors.textSecondary,
                color: colors.textSecondary
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddStaff} 
              variant="contained" 
              disabled={loading}
              sx={{ 
                background: colors.primary,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: colors.primary,
                  opacity: 0.9
                }
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : "Add Staff"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Password Modal */}
        <Dialog 
          open={isEditPasswordModalOpen} 
          onClose={() => setIsEditPasswordModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: `linear-gradient(135deg, ${colors.info}, ${colors.primary})`,
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <LockReset sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Reset Password for {selectedStaff?.firstName} {selectedStaff?.lastName}
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color={colors.textSecondary}>
                Enter a new password for this staff member. The password must be at least 6 characters long.
              </Typography>
            </Box>
            <TextField
              label="New Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={editPasswordData.newPassword}
              onChange={(e) => setEditPasswordData({ ...editPasswordData, newPassword: e.target.value })}
              required
              variant="outlined"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={editPasswordData.confirmPassword}
              onChange={(e) => setEditPasswordData({ ...editPasswordData, confirmPassword: e.target.value })}
              required
              variant="outlined"
            />
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setIsEditPasswordModalOpen(false)}
              variant="outlined"
              disabled={passwordLoading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: colors.textSecondary,
                color: colors.textSecondary
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmEditPassword} 
              variant="contained"
              disabled={!editPasswordData.newPassword || !editPasswordData.confirmPassword || passwordLoading}
              sx={{ 
                background: colors.info,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: colors.info,
                  opacity: 0.9
                }
              }}
            >
              {passwordLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : "Update Password"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Device Lock Confirmation Dialog */}
        <Dialog
          open={deviceLockDialog.open}
          onClose={() => setDeviceLockDialog({ open: false, staff: null, action: "enable" })}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: deviceLockDialog.action === "enable" ? colors.success : colors.warning,
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              {deviceLockDialog.action === "enable" ? 
                <Lock sx={{ fontSize: 28 }} /> : 
                <LockOpen sx={{ fontSize: 28 }} />
              }
              <Typography variant="h6" fontWeight="bold">
                {deviceLockDialog.action === "enable" ? 'Enable Device Locking' : 'Disable Device Locking'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar sx={{ 
                bgcolor: deviceLockDialog.action === "enable" ? colors.success : colors.warning, 
                width: 60, height: 60 
              }}>
                {deviceLockDialog.staff && getInitials(deviceLockDialog.staff.firstName, deviceLockDialog.staff.lastName)}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {deviceLockDialog.staff?.firstName} {deviceLockDialog.staff?.lastName}
                </Typography>
                <Typography variant="body2" color={colors.textSecondary}>
                  {deviceLockDialog.staff?.email}
                </Typography>
              </Box>
            </Box>
            
            {deviceLockDialog.action === "enable" ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Enabling</strong> device locking will:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Restrict staff to use only registered devices<br/>
                  • Enhance security by preventing unauthorized device access<br/>
                  • Require device verification for login<br/>
                  • Can be disabled at any time
                </Typography>
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Disabling</strong> device locking will:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Allow login from any device<br/>
                  • Remove device verification requirements<br/>
                  • May reduce security<br/>
                  • Can be re-enabled at any time
                </Typography>
              </Alert>
            )}

            <Typography variant="body2" color={colors.textSecondary}>
              {deviceLockDialog.action === "enable" 
                ? `Are you sure you want to enable device locking for ${deviceLockDialog.staff?.firstName}?`
                : `Are you sure you want to disable device locking for ${deviceLockDialog.staff?.firstName}?`
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setDeviceLockDialog({ open: false, staff: null, action: "enable" })}
              variant="outlined"
              disabled={deviceLockLoading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: colors.textSecondary,
                color: colors.textSecondary
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeviceLockToggle} 
              variant="contained"
              disabled={deviceLockLoading}
              sx={{ 
                background: deviceLockDialog.action === "enable" ? colors.success : colors.warning,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: deviceLockDialog.action === "enable" ? colors.success : colors.warning,
                  opacity: 0.9
                }
              }}
            >
              {deviceLockLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 
                (deviceLockDialog.action === "enable" ? 'Enable Device Locking' : 'Disable Device Locking')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, staff: null })}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: colors.error,
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <PersonRemove sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Delete Staff Member
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar sx={{ bgcolor: colors.primary, width: 60, height: 60 }}>
                {deleteDialog.staff && getInitials(deleteDialog.staff.firstName, deleteDialog.staff.lastName)}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {deleteDialog.staff?.firstName} {deleteDialog.staff?.lastName}
                </Typography>
                <Typography variant="body2" color={colors.textSecondary}>
                  {deleteDialog.staff?.email}
                </Typography>
                <Typography variant="body2" color={colors.textSecondary}>
                  {deleteDialog.staff?.phone}
                </Typography>
              </Box>
            </Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                This action cannot be undone
              </Typography>
            </Alert>
            <Typography>
              Are you sure you want to permanently delete <strong>{deleteDialog.staff?.firstName} {deleteDialog.staff?.lastName}</strong>? 
              This will remove all their data and access from the system.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setDeleteDialog({ open: false, staff: null })}
              variant="outlined"
              disabled={deleteLoading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: colors.textSecondary,
                color: colors.textSecondary
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteStaff} 
              variant="contained"
              disabled={deleteLoading}
              sx={{ 
                background: colors.error,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: colors.error,
                  opacity: 0.9
                }
              }}
            >
              {deleteLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : "Delete Permanently"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status Toggle Dialog */}
        <Dialog
          open={statusDialog.open}
          onClose={() => setStatusDialog({ open: false, staff: null })}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: statusDialog.staff && isStaffActive(statusDialog.staff.status) ? colors.warning : colors.success,
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              {statusDialog.staff && isStaffActive(statusDialog.staff.status) ? 
                <Pause sx={{ fontSize: 28 }} /> : 
                <PlayArrow sx={{ fontSize: 28 }} />
              }
              <Typography variant="h6" fontWeight="bold">
                {statusDialog.staff && isStaffActive(statusDialog.staff.status) ? 'Deactivate Staff Member' : 'Activate Staff Member'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar sx={{ 
                bgcolor: statusDialog.staff && isStaffActive(statusDialog.staff.status) ? colors.warning : colors.success, 
                width: 60, height: 60 
              }}>
                {statusDialog.staff && getInitials(statusDialog.staff.firstName, statusDialog.staff.lastName)}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {statusDialog.staff?.firstName} {statusDialog.staff?.lastName}
                </Typography>
                <Typography variant="body2" color={colors.textSecondary}>
                  {statusDialog.staff?.email}
                </Typography>
                <Chip
                  label={`Current Status: ${statusDialog.staff && formatStatusDisplay(statusDialog.staff.status)}`}
                  size="small"
                  sx={{
                    background: getStatusColor(statusDialog.staff?.status),
                    color: 'white',
                    fontWeight: 500,
                    mt: 1
                  }}
                />
              </Box>
            </Box>
            
            {statusDialog.staff && isStaffActive(statusDialog.staff.status) ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Deactivating</strong> this staff member will:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Revoke their system access immediately<br/>
                  • Prevent them from logging in<br/>
                  • Preserve all their data and records<br/>
                  • Can be reactivated at any time
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Activating</strong> this staff member will:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Restore their system access immediately<br/>
                  • Allow them to log in with their credentials<br/>
                  • Grant all their previous permissions<br/>
                  • Can be deactivated at any time
                </Typography>
              </Alert>
            )}

            <Typography variant="body2" color={colors.textSecondary}>
              {statusDialog.staff && isStaffActive(statusDialog.staff.status) 
                ? `Are you sure you want to deactivate ${statusDialog.staff.firstName}?`
                : `Are you sure you want to activate ${statusDialog.staff?.firstName}?`
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setStatusDialog({ open: false, staff: null })}
              variant="outlined"
              disabled={statusLoading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: colors.textSecondary,
                color: colors.textSecondary
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmStatusToggle} 
              variant="contained"
              disabled={statusLoading}
              sx={{ 
                background: statusDialog.staff && isStaffActive(statusDialog.staff.status) ? colors.warning : colors.success,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: statusDialog.staff && isStaffActive(statusDialog.staff.status) ? colors.warning : colors.success,
                  opacity: 0.9
                }
              }}
            >
              {statusLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : (statusDialog.staff && isStaffActive(statusDialog.staff.status) ? 'Deactivate Staff' : 'Activate Staff')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alert Dialog */}
        <Dialog
          open={alertDialog.open}
          onClose={() => setAlertDialog({ ...alertDialog, open: false })}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: getDialogColor(alertDialog.type),
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              {getDialogIcon(alertDialog.type)}
              <Typography variant="h6" fontWeight="bold">
                {alertDialog.title}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography>
              {alertDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setAlertDialog({ ...alertDialog, open: false })}
              variant="contained"
              sx={{ 
                background: getDialogColor(alertDialog.type),
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: getDialogColor(alertDialog.type),
                  opacity: 0.9
                }
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  )
}

export default StaffManagementCRM